// --- Create Ad ---
import {
  change as changeFormField,
  initialize as initializeForm,
} from 'redux-form';

import {
  EXISTING_VOICEOVER,
  NEW_VOICEOVER,
  UPLOADED_AUDIO,
  ALL_GENDERS_VALUE,
  FREE_TRIAL_NULL_VALUE,
  BG_MUSIC,
  EXISTING_VO_BG_MUSIC,
  CAMPAIGN_FORM_NAME,
  AUDIENCE_FORM_NAME,
} from 'config';
import {
  CAMPAIGN_FIELDS,
  AUDIENCE_FIELDS,
} from 'config/adCreation';
import {
  ACCOUNT_REDUX_FORM_ID,
} from 'config/account';

import { populateFields, updateFields } from 'actions/editable-ad';
import { validateFields } from 'actions/validation';
import { daysFromToday, endOfDay } from 'utils/dateHelper';
import { createDefaultAudioUploadObj } from 'config/uploadProperties';
import * as debugActions from 'actions/debug';
import { userFlagUpdated } from 'actions/featureFlags';
import { setUserAccount } from 'actions/user';
import { resetAdToPending } from 'api/debug';
import { getWgToken as getWgTokenSelector } from 'selectors/auth';
import { getCampaignInitialValues, getAudienceInitialValues } from 'selectors/adCreation';

export const FORM_DEFS = {
  [CAMPAIGN_FORM_NAME]: {
    fields: CAMPAIGN_FIELDS,
    initialValuesSelector: getCampaignInitialValues,
  },
  [AUDIENCE_FORM_NAME]: {
    fields: AUDIENCE_FIELDS,
    initialValuesSelector: getAudienceInitialValues,
  },
};

export const POPULATED_STATE = {
  name: 'Get Spotify Premium',
  clickthroughUrl: 'https://www.spotify.com/watch-now/learn-more/',
  internalName: 'Test-Reject',
  objective: 'businesspromo',
  uploads: {
    image: {
      uploaded: true,
      uploadInfo: {
        id: '5b6983a9-d6f6-4ff2-b9ce-a54a5bd37289',
        action: 'https://cream-adgen.s3.amazonaws.com/',
        fields: {},
        uri:
          'https://cream-adgen.s3.amazonaws.com/assets/image/5b6983a9-d6f6-4ff2-b9ce-a54a5bd37289/asset?&cachebuster=1464017380754',
      },
    },
    [UPLOADED_AUDIO]: {
      uploaded: true,
      uploadInfo: {
        id: '123ad0a4-8b6c-4964-a1ba-c75d27cade3c',
        action: 'https://cream-adgen.s3.amazonaws.com/',
        fields: {},
        uri:
          'https://cream-adgen.s3.amazonaws.com/assets/audiocomponent/123ad0a4-8b6c-4964-a1ba-c75d27cade3c/asset',
      },
    },
    [BG_MUSIC]: createDefaultAudioUploadObj(),
    [EXISTING_VO_BG_MUSIC]: createDefaultAudioUploadObj(),
  },
  audioSubmission: {
    active: UPLOADED_AUDIO,
    [EXISTING_VOICEOVER]: {
      data: {
        id: '0e45c5b3-2804-4d63-9325-24dc8ae06a9a',
        name: 'ExistingVOTestName',
        uri:
          'https://drdxyzdg0sjtu.cloudfront.net/assets/audiocomponent/0e45c5b3-2804-4d63-9325-24dc8ae06a9a/asset',
      },
      bgMusic: {},
    },
    [NEW_VOICEOVER]: {
      name: '',
      transcript: '',
      instructions: '',
      locale: '',
      voice: '',
      bgMusic: {},
    },
  },
  targeting: {
    locations: [
      {
        countryCode: 'US',
        id: 'dfp:2840',
        name: 'United States',
        parentId: '',
        type: 'country',
      },
    ],
    platforms: {
      desktop: true,
      ios: false,
      android: false,
    },
    gender: ALL_GENDERS_VALUE,
    ageRange: {
      start: '13',
      end: '65',
    },
    fanAudiences: [],
    moments: [],
    genres: [],
    freeTrial: FREE_TRIAL_NULL_VALUE,
  },
  dateRange: {
    begin: daysFromToday(2),
    end: endOfDay(daysFromToday(2)),
  },
  payments: {
    totalBudget: '0.03',
  },
};

export const POPULATED_ACCOUNT = {
  country: 'United States',
  firstName: 'Buddy',
  lastName: 'The Elf',
  jobTitle: 'Account Filler Fairy',
  businessName: 'All About That Form Life',
  businessWebsite: 'http://buddytheelfformlife.com',
  industry: 'Automotive',
  adStudioLead: 'UI Team Rules!',
};

const KEY_F6 = 117;
const KEY_F7 = 118;

export default function debug(store, windowObj) {
  function debugPopulateFields() {
    store.dispatch(populateFields(POPULATED_STATE));
    store.dispatch(
      validateFields({
        fields: POPULATED_STATE,
        groups: store.getState().user.groups,
      })
    );

    // add the values to the redux forms as well
    // TODO consolidate this with the restore logic
    Object.keys(FORM_DEFS).forEach(formName => {
      const { fields, initialValuesSelector } = FORM_DEFS[formName];
      store.dispatch(
        initializeForm(formName, initialValuesSelector(store.getState()))
      );
      fields.forEach(fieldName =>
        store.dispatch(
          changeFormField(formName, fieldName, POPULATED_STATE[fieldName])
        )
      );
    });
  }

  function populateAccountFields() {
    Object.keys(POPULATED_ACCOUNT).forEach(fieldName =>
      store.dispatch(
        changeFormField(ACCOUNT_REDUX_FORM_ID, fieldName, POPULATED_ACCOUNT[fieldName])
      )
    );
  }

  function forceAccountSetup() {
    store.dispatch(setUserAccount(null));
  }

  function getWgToken() {
    return getWgTokenSelector(store.getState());
  }

  // Add debug keyboard shortcuts
  windowObj.onkeyup = e => {
    switch (e.keyCode) {
      case KEY_F6:
        debugPopulateFields();
        break;
      case KEY_F7:
        populateAccountFields();
        break;
      default:
    }
  };

  function setFeatureFlag(flag, value) {
    store.dispatch(userFlagUpdated({ value, flag }));
  }

  function updateField(key, value) {
    store.dispatch(updateFields({ key, value }));
    store.dispatch(
      validateFields({
        fields: POPULATED_STATE,
        groups: store.getState().user.groups,
      })
    );
  }

  function triggerAction(actionType, payload) {
    store.dispatch({
      type: actionType,
      payload,
    });
  }

  function extendStartDate(adId, numDays = 30) {
    store.dispatch(debugActions.extendStartDate(adId, daysFromToday(numDays)));
  }

  function extendEndDate(adId, numDays = 30) {
    store.dispatch(debugActions.extendEndDate(adId, daysFromToday(numDays)));
  }

  function fakeUploadFullMix() {
    updateField('uploads.fullmix.uploaded', true);
    updateField(
      'uploads.fullmix.uploadInfo.id',
      '123ad0a4-8b6c-4964-a1ba-c75d27cade3c'
    );
    updateField(
      'uploads.fullmix.uploadInfo.uri',
      'https://cream-adgen.s3.amazonaws.com/assets/audiocomponent/123ad0a4-8b6c-4964-a1ba-c75d27cade3c/asset'
    );
  }

  function fakeUploadCompanionImage() {
    updateField('uploads.image.uploaded', true);
    updateField(
      'uploads.image.uploadInfo.id',
      '566359fc-fcd5-46f2-bc1e-8a1a84762a4a'
    );
    updateField(
      'uploads.image.uploadInfo.uri',
      'https://cream-adgen.s3.amazonaws.com/assets/image/566359fc-fcd5-46f2-bc1e-8a1a84762a4a/asset?&cachebuster=1464017380754'
    );
  }

  return {
    populateFields: debugPopulateFields,
    populateAccount: populateAccountFields,
    forceAccountSetup,
    store,
    env: () => __ENV__,
    setFeatureFlag,
    updateField,
    triggerAction,
    resetAdToPending: adId =>
      resetAdToPending(store.getState().auth.token, adId),
    fakeUploadFullMix,
    fakeUploadCompanionImage,
    extendStartDate,
    extendEndDate,
    getWgToken,
  };
}



// WEBPACK FOOTER //
// ./src/config/debug.js